import { useCallback, useEffect, useState } from "react";
import {
  navGroupStorageKey,
  parseCollapsedGroups,
  serializeCollapsedGroups,
  toggleCollapsedGroup
} from "../nav-model";

export interface CollapsedNavGroups {
  isGroupCollapsed: (groupId: string) => boolean;
  toggleGroup: (groupId: string) => void;
}

/**
 * Tracks which nav groups the user has collapsed, persisted per app under the
 * same localStorage key the earlier shell navigation used so existing state carries over.
 * All the parsing/pruning logic lives in nav-model, keeping this a thin glue layer.
 */
export function useCollapsedNavGroups(
  storageKey: string | null,
  knownGroupIds: string[]
): CollapsedNavGroups {
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());

  // Reload whenever the app changes: each app keeps its own collapsed sections.
  useEffect(() => {
    const key = navGroupStorageKey(storageKey);

    if (!key) {
      setCollapsed(new Set());
      return;
    }

    try {
      setCollapsed(parseCollapsedGroups(localStorage.getItem(key)));
    } catch {
      // Storage inaccessible (private mode, quota)  -  fall back to all expanded.
      setCollapsed(new Set());
    }
  }, [storageKey]);

  const toggleGroup = useCallback(
    (groupId: string): void => {
      setCollapsed((current) => {
        const next = toggleCollapsedGroup(current, groupId);
        const key = navGroupStorageKey(storageKey);

        if (key) {
          try {
            const serialized = serializeCollapsedGroups(next, knownGroupIds);

            if (serialized === null) {
              localStorage.removeItem(key);
            } else {
              localStorage.setItem(key, serialized);
            }
          } catch {
            // Storage full or inaccessible  -  keep the in-memory state anyway.
          }
        }

        return next;
      });
    },
    [knownGroupIds, storageKey]
  );

  const isGroupCollapsed = useCallback(
    (groupId: string): boolean => collapsed.has(groupId),
    [collapsed]
  );

  return { isGroupCollapsed, toggleGroup };
}
